import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { CheckCircle, Award, Users, Globe, ArrowRight, Linkedin, Mail, Phone, MapPin } from 'lucide-react';

const About = () => {
  return (
    <div className="about-page">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Expert Trade Compliance<br />Guidance Since 2009
            </h1>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
              Blackbird Trade Group delivers sophisticated trade compliance solutions to companies navigating the complexities of international regulations. With decades of combined experience, we provide strategic guidance that protects and enables global business operations.
            </p>
          </div>
        </div>
      </section>

      {/* Leadership Profile */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Leadership</h2>
            <p className="text-lg text-gray-600">Meet the expert behind Blackbird Trade Group</p>
          </div>

          <div className="max-w-4xl bg-gray-50 border border-gray-200 rounded-lg p-8">
            <div>
              {/* Contact Info - No Photo */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">Nancy Gazzano</h3>
                <p className="text-lg text-gray-700 mb-3">Founder & Principal | International Trade Compliance</p>
                <p className="flex items-center text-gray-600 text-sm mb-4">
                  <MapPin className="h-4 w-4 mr-1" />
                  Jacksonville, FL
                </p>

                {/* Contact Links */}
                <div className="flex flex-wrap items-center gap-4 mb-8">
                  <a 
                    href="https://www.linkedin.com/in/nancy-gazzano-28841112"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-[#0A66C2] hover:underline font-medium"
                  >
                    <Linkedin className="h-4 w-4" fill="currentColor" />
                    LinkedIn Profile
                  </a>
                  <a 
                    href="mailto:nwood@blackbirdtradegroup.com"
                    className="inline-flex items-center gap-2 text-sm text-gray-700 hover:text-slate-900 font-medium"
                  >
                    <Mail className="h-4 w-4" />
                    nwood@blackbirdtradegroup.com
                  </a>
                  <a 
                    href="tel:+19045121329"
                    className="inline-flex items-center gap-2 text-sm text-gray-700 hover:text-slate-900 font-medium"
                  >
                    <Phone className="h-4 w-4" />
                    +1 (904) 512-1329
                  </a>
                </div>

                {/* Bio */}
                <div className="text-left text-gray-700 leading-relaxed space-y-4 text-base">
                  <p>
                    Nancy Gazzano founded Blackbird Trade Group with a mission to provide accessible, expert trade compliance guidance to companies of all sizes. With over 15 years of experience in international trade regulations, she has built a reputation for practical solutions that balance regulatory requirements with business objectives.
                  </p>
                  <p>
                    Her expertise spans the full spectrum of trade compliance, including export controls (ITAR and EAR), customs regulations, product classification, sanctions screening, and Foreign Trade Zone operations. Nancy has advised companies across diverse industries—from aerospace and defense to medical devices and industrial manufacturing—on complex multijurisdictional regulatory requirements.
                  </p>
                  <p>
                    Prior to establishing Blackbird Trade Group, Nancy held senior compliance positions where she developed and managed comprehensive trade compliance programs, conducted internal audits, and successfully navigated voluntary disclosures with government authorities. Her approach combines deep regulatory knowledge with an understanding of operational realities, ensuring compliance solutions that work in practice, not just on paper.
                  </p>
                  <p>
                    Nancy is committed to staying at the forefront of evolving trade regulations and maintaining the highest standards of professional service. She regularly provides training on export controls, import compliance, and best practices in trade operations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Our Approach
            </h2>
            <p className="text-lg text-gray-600">
              We believe effective trade compliance requires more than regulatory knowledge—it demands practical experience, strategic thinking, and a commitment to understanding each client's unique business environment.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-6">
                <CheckCircle className="h-6 w-6 text-slate-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Practical Compliance</h3>
              <p className="text-gray-600 leading-relaxed">
                We deliver solutions designed for real-world implementation, not theoretical ideals. Our recommendations are actionable, sustainable, and aligned with your operational realities.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-6">
                <Award className="h-6 w-6 text-slate-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Deep Expertise</h3>
              <p className="text-gray-600 leading-relaxed">
                Years of hands-on experience across multiple regulatory frameworks and industries. We understand the nuances that distinguish compliant operations from regulatory violations.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-6">
                <Users className="h-6 w-6 text-slate-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Client Partnership</h3>
              <p className="text-gray-600 leading-relaxed">
                Direct access to senior-level expertise. No tiered service models or junior consultants. When you work with Blackbird, you work with Nancy—an experienced professional invested in your success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience & Expertise */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Areas of Expertise</h2>
              <div className="space-y-4">
                {[
                  'Export Administration Regulations (EAR) and ITAR Compliance',
                  'Product Classification (HTS, ECCN, USML) across Global Jurisdictions',
                  'Export License Applications and Jurisdiction Determinations',
                  'Customs Import Compliance and Tariff Optimization',
                  'OFAC Sanctions Screening and Restricted Party Management',
                  'Foreign Trade Zone (FTZ) Operations and Activation',
                  'Voluntary Disclosure Strategy and Preparation',
                  'Trade Compliance Program Development and Auditing',
                  'Export Control and Import Compliance Training',
                  'Managed Trade Compliance Services'
                ].map((item, i) => (
                  <div key={i} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Industries Served</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Blackbird Trade Group has provided compliance guidance across a diverse range of industries, each with unique regulatory challenges and business requirements.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  'Aerospace & Defense',
                  'Medical Devices',
                  'Industrial Manufacturing',
                  'Electronics & Technology',
                  'Automotive Components',
                  'Chemical & Materials',
                  'Oil & Gas Equipment',
                  'Consumer Products',
                  'Telecommunications',
                  'Maritime & Transportation'
                ].map((industry, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <p className="text-gray-800 font-medium text-sm">{industry}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-slate-700 mb-2">Since 2009</div>
              <div className="text-gray-600">Advising Clients</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-slate-700 mb-2">500+</div>
              <div className="text-gray-600">Classifications Completed</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-slate-700 mb-2">100+</div>
              <div className="text-gray-600">License Applications</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-slate-700 mb-2">Global</div>
              <div className="text-gray-600">Jurisdictions Supported</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Strengthen Your Trade Compliance?
          </h2>
          <p className="text-base sm:text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Partner with an experienced trade compliance professional who understands your challenges.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
                Schedule a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <a href="mailto:nwood@blackbirdtradegroup.com">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 h-14 font-semibold">
                Email Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
